'use client';

import type { ReactNode } from 'react';
import { SWRConfig } from 'swr';

/**
 * SWR 캐시 인스턴스
 * 모듈 스코프에 생성하여 re-render 시에도 동일한 캐시 유지
 */
const swrCache = new Map();

/**
 * SWR 전역 설정 Provider
 *
 * 애플리케이션 전체에서 사용할 SWR 기본 설정을 정의합니다.
 * - 캐싱 전략
 * - 재검증 동작
 * - 에러 처리
 * - 성능 최적화
 */
export function SWRProvider({ children }: { children: ReactNode }) {
  return (
    <SWRConfig
      value={{
        /**
         * 중복 요청 제거 간격 (ms)
         * 같은 키에 대한 요청이 이 시간 내에 발생하면 중복 제거
         */
        dedupingInterval: 2000,

        /**
         * 포커스 시 자동 재검증
         * Supabase 조회는 true, API 조회는 개별 설정
         */
        revalidateOnFocus: true,

        /**
         * 재연결 시 자동 재검증
         * 네트워크 재연결 시 데이터 최신화
         */
        revalidateOnReconnect: true,

        /**
         * 마운트 시 자동 재검증
         * 컴포넌트가 마운트될 때 캐시된 데이터가 있어도 재검증
         */
        revalidateOnMount: true,

        /**
         * 에러 발생 시 재시도 간격 계산
         * 지수 백오프(exponential backoff) 전략
         */
        onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
          // 404 에러는 재시도하지 않음
          if (error.status === 404) {
            return;
          }

          // 최대 3회까지만 재시도
          if (retryCount >= 3) {
            return;
          }

          // 지수 백오프: 1초, 2초, 4초
          const timeout = Math.min(1000 * 2 ** retryCount, 8000);
          setTimeout(() => revalidate({ retryCount }), timeout);
        },

        /**
         * 에러 발생 시 기존 데이터 유지
         * 네트워크 에러 시에도 사용자 경험 유지
         */
        keepPreviousData: true,

        /**
         * SWR 내부 에러 핸들러
         * 개발 환경에서만 로깅
         */
        onError: (error, key) => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error(`SWR Error [${key}]:`, error);
          }
        },

        /**
         * 성공 시 글로벌 핸들러 (선택적)
         * 필요시 analytics 등에 활용 가능
         */
        // onSuccess: (data, key, config) => {
        //   // Analytics tracking 등
        // },

        /**
         * 로딩 타임아웃 (ms)
         * 이 시간이 지나면 로딩 상태 표시
         */
        loadingTimeout: 3000,

        /**
         * 에러 타임아웃 (ms)
         * 이 시간이 지나면 에러 상태 표시
         */
        errorRetryInterval: 5000,

        /**
         * 에러 재시도 횟수
         * 개별 hook에서 override 가능
         */
        errorRetryCount: 3,

        /**
         * 폴링 간격 (ms)
         * 기본적으로 비활성화, 필요시 개별 hook에서 설정
         */
        refreshInterval: 0,

        /**
         * 창이 보이지 않을 때 폴링 중지
         */
        refreshWhenHidden: false,

        /**
         * 오프라인 시 폴링 중지
         */
        refreshWhenOffline: false,

        /**
         * 일시 중단(suspend) 시 재검증 하지 않음
         * React Suspense와 함께 사용 시
         */
        revalidateIfStale: true,

        /**
         * 오래된 데이터 표시하면서 백그라운드 재검증
         * 빠른 초기 로딩 + 데이터 최신성 보장
         */
        fallbackData: undefined,

        /**
         * 비교 함수 (데이터 변경 감지)
         * 기본 참조 비교 사용
         */
        compare: (a, b) => {
          // 같은 참조면 변경 없음
          return a === b;
        },

        /**
         * Provider 레벨에서 캐시 접근 가능
         * 모듈 스코프의 동일한 Map 인스턴스 반환하여 re-render 시에도 캐시 유지
         */
        provider: () => swrCache,
      }}
    >
      {children}
    </SWRConfig>
  );
}
